import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Wallet, Plus, Paperclip, Trash2, X, CheckCircle2, Clock, XCircle, TrendingUp, TrendingDown, Upload, TrendingDown as TrendChart, Sparkles } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const CURR = ["USD", "EUR", "GBP", "INR"];
const SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const METHODS = ["bank", "upi", "card", "cash", "other"];
const MAX_ATTACH_KB = 200;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const statusChip = (s) => {
  if (s === "verified") return { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Verified" };
  if (s === "rejected") return { cls: "bg-red-100 text-red-800 border-red-200", icon: XCircle, label: "Rejected" };
  return { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock, label: "Pending review" };
};

export default function PaymentsTab() {
  const [balance, setBalance] = useState(null);
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [bookings, setBookings] = useState([]); // partner's invoiceable bookings for the log-payment picker
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const csvRef = useRef();
  const [form, setForm] = useState({
    amount: "", currency: "USD", method: "bank",
    reference: "", note: "", entry_date: new Date().toISOString().slice(0, 10),
    booking_id: "", booking_kind: "",
  });
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Pay-All-Due preview modal — `null` when closed, else `{ target, rows }`
  // holding the currency being paid and the ordered list of unpaid
  // bookings that will get funds. Confirming stamps the form; cancel
  // just drops back to the picker.
  const [payAllPreview, setPayAllPreview] = useState(null);
  const fileRef = useRef();

  // Stable identity via useCallback so the effect below can list it
  // as a dep without triggering an infinite reload loop.
  const load = useCallback(async () => {
    const [b, p, h, hs] = await Promise.all([
      api.get("/partner-portal/balance").catch(() => ({ data: null })),
      api.get("/partner-portal/payments").catch(() => ({ data: [] })),
      api.get("/partner-portal/balance-history?months=6").catch(() => ({ data: { points: [] } })),
      api.get("/partner-portal/home-summary").catch(() => ({ data: { invoiceable_bookings: [] } })),
    ]);
    setBalance(b.data);
    setRows(p.data || []);
    setHistory(h.data?.points || []);
    setBookings(hs.data?.invoiceable_bookings || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/partner-portal/payments/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(data);
      toast.success(`Imported ${data.imported} payment${data.imported === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const pickFile = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_ATTACH_KB * 1024) {
      toast.error(`Receipt must be under ${MAX_ATTACH_KB} KB`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setAttachment(f);
  };

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Enter a positive amount");
    let file_id = null;
    if (attachment) {
      setUploading(true);
      try {
        const fd = new FormData(); fd.append("file", attachment);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_id = data.id;
      } catch {
        toast.error("Receipt upload failed"); setUploading(false); return;
      }
      setUploading(false);
    }
    try {
      const multi = (form.booking_ids || []).filter(Boolean);
      if (multi.length > 1) {
        // Fan out to backend as an array — backend will split the amount
        // evenly, create one partner_payments row per booking, and snapshot
        // the customer + programme onto each row so the log renders names.
        const bookings_payload = multi.map(id => ({ id, kind: form._kindsById?.[id] || "" }));
        await api.post("/partner-portal/payments", { ...form, amount: parseFloat(form.amount), file_id, bookings: bookings_payload });
        toast.success(`Payment logged across ${multi.length} bookings — team will verify shortly`);
      } else {
        await api.post("/partner-portal/payments", { ...form, amount: parseFloat(form.amount), file_id });
        toast.success("Payment logged — team will verify shortly");
      }
      setForm({ amount: "", currency: "USD", method: "bank", reference: "", note: "", entry_date: new Date().toISOString().slice(0, 10), booking_id: "", booking_kind: "", booking_ids: [], _kindsById: {} });
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = "";
      setAdding(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const PAYMENT_REASONS = ["Chargeback", "Duplicate entry", "Correction", "Refunded to participant", "Other"];
  const [delModal, setDelModal] = useState(null); // { row, reason: "", note: "" }
  const del = (r) => setDelModal({ row: r, reason: "", note: "" });
  const submitDel = async () => {
    if (!delModal) return;
    const r = delModal.row;
    const reasonText = delModal.reason === "Other" ? (delModal.note || "Other").trim() : delModal.reason;
    try {
      const q = reasonText ? `?reason=${encodeURIComponent(reasonText)}` : "";
      const { data } = await api.delete(`/partner-portal/payments/${r.id}${q}`);
      toast.success(data.was_verified
        ? "Verified payment deleted — snapshotted to Recycle Bin. Founder notified."
        : "Removed");
      setDelModal(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Only currencies that actually appear in any bucket — keeps the summary
  // cards focused instead of showing four empty tiles.
  const activeCurrencies = useMemo(() => {
    if (!balance) return [];
    const set = new Set();
    for (const key of ["invoiced", "paid", "balance"]) {
      for (const c of Object.keys(balance[key] || {})) {
        if ((balance[key][c] || 0) !== 0) set.add(c);
      }
    }
    return CURR.filter(c => set.has(c));
  }, [balance]);

  // Trend series — one Recharts row per month, flattened per currency so
  // we can plot up to 4 lines. Currencies with zero activity are hidden.
  // Also includes a single USD-aggregated line for the "One clean number"
  // view that most partners prefer over four separate currency lines.
  const trendData = useMemo(() => {
    if (!history.length) return [];
    return history.map(p => {
      const row = { month: p.month, "USD equiv.": p.usd_total || 0 };
      for (const c of activeCurrencies) row[c] = p.balance?.[c] || 0;
      return row;
    });
  }, [history, activeCurrencies]);
  const CHART_COLORS = { USD: "#0EA5E9", EUR: "#8B5CF6", GBP: "#EF4444", INR: "#059669", "USD equiv.": "#111827" };
  const [chartMode, setChartMode] = useState("usd");  // 'usd' | 'per-currency'

  return (
    <div className="space-y-4" data-testid="partner-payments-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Wallet size={11}/> Payments</div>
          <h2 className="text-xl font-display font-semibold mt-0.5">Your invoice balance</h2>
          <p className="text-xs text-[color:var(--aii-muted)] mt-1">
            Log each payment you make against your invoices. The team verifies within 24h and the balance updates the moment your entry is approved.
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          data-testid="pp-payment-add"
          className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5">
          <Plus size={14}/> Log payment
        </button>
        <input ref={csvRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="pp-import-csv-input"/>
        <button
          onClick={() => csvRef.current?.click()}
          disabled={importing}
          data-testid="pp-import-csv"
          title="Bulk-log historical payments from a CSV (date, amount, currency, method, reference, note)"
          className="text-sm px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5 disabled:opacity-50">
          <Upload size={14}/> {importing ? "Importing…" : "Import CSV"}
        </button>
      </div>

      {importReport && (
        <div className="aii-card p-4 border-l-4 border-emerald-500 text-sm" data-testid="pp-import-report">
          <div className="flex items-center justify-between mb-1">
            <b>CSV import summary</b>
            <button onClick={() => setImportReport(null)}><X size={14}/></button>
          </div>
          Imported <b>{importReport.imported}</b>{importReport.skipped ? ` · Skipped ${importReport.skipped}` : ""}.
          {importReport.errors && importReport.errors.length > 0 && (
            <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
              {importReport.errors.slice(0, 8).map((e, i) => <li key={`imperr-${i}-${String(e).slice(0, 24)}`}>{e}</li>)}
            </ul>
          )}
        </div>
      )}

      {trendData.length >= 2 && (
        <div className="aii-card p-5" data-testid="pp-trend-chart">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <TrendChart size={14} className="text-brand"/>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">6-month trend</div>
                <div className="text-sm font-semibold">
                  {chartMode === "usd" ? "Balance in USD (all currencies combined)" : "Balance by currency"}
                </div>
              </div>
            </div>
            <div className="inline-flex text-[11px] border border-[color:var(--aii-border)] rounded-md overflow-hidden">
              <button data-testid="pp-trend-usd" onClick={() => setChartMode("usd")}
                className={`px-2.5 py-1 font-semibold ${chartMode === "usd" ? "bg-brand text-white" : "bg-white text-[color:var(--aii-text-2)]"}`}>USD equiv.</button>
              <button data-testid="pp-trend-percur" onClick={() => setChartMode("per-currency")}
                className={`px-2.5 py-1 font-semibold ${chartMode === "per-currency" ? "bg-brand text-white" : "bg-white text-[color:var(--aii-text-2)]"}`}>Per currency</button>
            </div>
          </div>
          <div className="w-full h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
                <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}/>
                <Tooltip formatter={(v, name) => [`${chartMode === "usd" || name === "USD equiv." ? "$" : (SYM[name] || name)}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`, name]}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
                {chartMode === "usd" ? (
                  <Line type="monotone" dataKey="USD equiv." stroke={CHART_COLORS["USD equiv."]} strokeWidth={2.5} dot={{ r: 4 }}/>
                ) : (
                  activeCurrencies.map(c => (
                    <Line key={c} type="monotone" dataKey={c} stroke={CHART_COLORS[c] || "#0EA5E9"} strokeWidth={2} dot={{ r: 3 }}/>
                  ))
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-[color:var(--aii-muted)] mt-2 flex items-center gap-1">
            <TrendingDown size={10} className="text-emerald-600"/> A line trending down means you&apos;re settling up — nice work.
          </div>
        </div>
      )}

      {balance && activeCurrencies.length === 0 && (
        <div className="aii-card p-8 text-center text-sm text-[color:var(--aii-muted)] italic" data-testid="pp-balance-empty">
          Nothing to pay yet. Your balance appears here as soon as our team prices an application or a weekend tour on your account.
        </div>
      )}

      {activeCurrencies.length > 0 && (
        <div className="space-y-2" data-testid="pp-balance-cards">
          {activeCurrencies.map(c => {
            const invoiced = balance.invoiced?.[c] || 0;
            const paid = balance.paid?.[c] || 0;
            const bal = balance.balance?.[c] || 0;
            const settled = bal <= 0.01;
            return (
              <div key={c} data-testid={`pp-balance-${c}`} className={`aii-card p-4 border-l-4 ${settled ? "border-emerald-500" : "border-brand"}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold">{c} balance</div>
                    <div className={`text-3xl font-display font-semibold ${settled ? "text-emerald-700" : "text-brand"}`} data-testid={`pp-balance-amt-${c}`}>
                      {SYM[c] || c}{Number(bal).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-[11px] text-[color:var(--aii-muted)] mt-0.5">
                      {settled ? "Fully settled — thank you." : "Amount still owed"}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="px-3 py-2 rounded-md border border-sky-200 bg-sky-50 text-right">
                      <div className="text-[9px] uppercase tracking-widest text-sky-800 font-semibold flex items-center gap-1"><TrendingUp size={9}/> Invoiced</div>
                      <div className="text-sm font-semibold text-sky-900">{SYM[c] || c}{Number(invoiced).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div className="px-3 py-2 rounded-md border border-emerald-200 bg-emerald-50 text-right">
                      <div className="text-[9px] uppercase tracking-widest text-emerald-800 font-semibold flex items-center gap-1"><TrendingDown size={9}/> Paid</div>
                      <div className="text-sm font-semibold text-emerald-900">{SYM[c] || c}{Number(paid).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <div className="aii-card p-5" data-testid="pp-payment-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold">Log a payment</h3>
            <button onClick={() => setAdding(false)}><X size={16}/></button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Amount *</label>
              <input data-testid="pp-payment-amount" type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Currency</label>
              <select data-testid="pp-payment-currency" value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                {CURR.map(c => <option key={c} value={c}>{SYM[c]} {c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Method</label>
              <select data-testid="pp-payment-method" value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                {METHODS.map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Date</label>
              <input data-testid="pp-payment-date" type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Reference / Txn ID</label>
              <input data-testid="pp-payment-reference" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="Bank txn no." className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Tag against booking <span className="text-[color:var(--aii-muted)] font-normal">(optional — helps us know whose invoice you&apos;re paying)</span></label>
              <select data-testid="pp-payment-booking" value={form.booking_id ? `${form.booking_id}|${form.booking_kind}` : ""}
                onChange={e => {
                  const [bid, kind] = (e.target.value || "").split("|");
                  const b = bookings.find(x => x.id === bid);
                  setForm(f => ({
                    ...f, booking_id: bid || "", booking_kind: kind || "",
                    // auto-fill amount + currency from the booking if amount is still blank
                    amount: (f.amount || "") === "" && b ? String(b.amount) : f.amount,
                    currency: b?.currency || f.currency,
                  }));
                }}
                className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                <option value="">— Unallocated / pick multiple below —</option>
                {bookings.filter(b => Number(b.balance ?? b.amount) > 0).map(b => (
                  <option key={`${b.id}-${b.kind}`} value={`${b.id}|${b.kind}`}>
                    {b.kind === "weekend" ? "🗺️" : "✈️"} {b.customer} · {b.programme} · {b.start_date || "—"} · {SYM[b.currency] || b.currency}{Number(b.balance ?? b.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </option>
                ))}
              </select>
              {/* Multi-select — pick multiple participant bookings and the
                  submitted amount will be split evenly across them; each
                  split becomes its own row in the log below with the
                  participant name + programme visible. */}
              <div className="mt-2 border border-[color:var(--aii-border)] rounded-md bg-white p-2 max-h-40 overflow-y-auto" data-testid="pp-multi-bookings">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--aii-muted)] font-semibold">
                    Or split this payment across multiple bookings
                  </div>
                  {/* One-tap "Pay all due" — ticks every unpaid booking in
                      the current form.currency (defaults to the majority
                      currency among unpaid rows if none picked yet) and
                      auto-sums the outstanding balance into the Amount
                      field. Only shows when there's at least 2 unpaid
                      rows to make a meaningful bulk pick. */}
                  {(() => {
                    const unpaid = bookings.filter(b => Number(b.balance ?? b.amount) > 0);
                    if (unpaid.length < 2) return null;
                    const counts = unpaid.reduce((m, b) => { m[b.currency] = (m[b.currency] || 0) + 1; return m; }, {});
                    const majority = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
                    const target = unpaid.some(b => b.currency === form.currency) ? form.currency : majority;
                    const rows = unpaid.filter(b => b.currency === target);
                    if (rows.length < 2) return null;
                    return (
                      <button
                        type="button"
                        data-testid="pp-pay-all-due"
                        onClick={() => setPayAllPreview({ target, rows })}
                        className="text-[10px] px-2 py-0.5 rounded bg-brand text-white font-bold hover:bg-brand-dark whitespace-nowrap">
                        ⚡ Pay all {rows.length} due · {SYM[target] || target}
                      </button>
                    );
                  })()}
                </div>
                {(() => {
                  // Currency guard — if the tickeded set already has 1+
                  // rows, disable the checkbox on any candidate row whose
                  // currency doesn't match the first-picked row. Show a
                  // small warning banner so the partner knows why the
                  // mismatched rows are inert.
                  const ids = form.booking_ids || [];
                  const lockedCur = ids.length ? (bookings.find(x => x.id === ids[0])?.currency || null) : null;
                  const mismatched = lockedCur
                    ? bookings.filter(b => Number(b.balance ?? b.amount) > 0 && b.currency !== lockedCur).length
                    : 0;
                  return mismatched > 0 ? (
                    <div data-testid="pp-currency-guard"
                         className="text-[10px] bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1 mb-1">
                      ⚠ Locked to {SYM[lockedCur] || lockedCur}{lockedCur} — {mismatched} row(s) in other currencies are disabled. Save this payment first, then log the next currency separately.
                    </div>
                  ) : null;
                })()}
                {bookings.length === 0 && <div className="text-[11px] italic text-[color:var(--aii-muted)]">No open bookings.</div>}
                {bookings.filter(b => Number(b.balance ?? b.amount) > 0).map(b => {
                  const checked = (form.booking_ids || []).includes(b.id);
                  const ids = form.booking_ids || [];
                  const lockedCur = ids.length ? (bookings.find(x => x.id === ids[0])?.currency || null) : null;
                  const disabled = lockedCur && b.currency !== lockedCur && !checked;
                  return (
                    <label key={`multi-${b.id}-${b.kind}`}
                           className={`flex items-center gap-2 text-xs py-1 cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-50"}`}
                           title={disabled ? `Currency locked to ${lockedCur} for this payment` : ""}>
                      <input type="checkbox" data-testid={`pp-multi-${b.id}`}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => setForm(f => {
                          const arrIds = new Set(f.booking_ids || []);
                          const arrKinds = { ...(f._kindsById || {}) };
                          if (arrIds.has(b.id)) { arrIds.delete(b.id); delete arrKinds[b.id]; }
                          else { arrIds.add(b.id); arrKinds[b.id] = b.kind; }
                          const nextIds = [...arrIds];
                          const sumOutstanding = nextIds.reduce((tot, bid) => {
                            const row = bookings.find(x => x.id === bid);
                            const bal = row ? Number(row.balance ?? row.amount ?? 0) : 0;
                            return tot + (bal > 0 ? bal : 0);
                          }, 0);
                          const firstCur = nextIds.length ? (bookings.find(x => x.id === nextIds[0])?.currency || f.currency) : f.currency;
                          return {
                            ...f, booking_ids: nextIds, _kindsById: arrKinds,
                            booking_id: "", booking_kind: "",
                            currency: firstCur,
                            amount: nextIds.length ? String(Math.round(sumOutstanding)) : f.amount,
                          };
                        })}/>
                      <span>{b.kind === "weekend" ? "🗺️" : "✈️"} <b>{b.customer}</b> · {b.programme} · {b.start_date || "—"} · {SYM[b.currency] || b.currency}{Number(b.balance ?? b.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}{b.paid > 0 && b.balance > 0 ? <span className="text-[10px] text-amber-700 font-semibold ml-1">· {SYM[b.currency] || b.currency}{Number(b.paid).toLocaleString(undefined, { maximumFractionDigits: 0 })} paid</span> : null}</span>
                    </label>
                  );
                })}
                {(form.booking_ids || []).length > 1 && (
                  <div className="text-[10px] mt-1 px-1 text-emerald-800 font-semibold">
                    {form.booking_ids.length} bookings selected — {SYM[form.currency] || form.currency}{(parseFloat(form.amount || 0) / form.booking_ids.length).toFixed(2)} each
                  </div>
                )}
              </div>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Note</label>
              <input data-testid="pp-payment-note" value={form.note}
                onChange={e => {
                  const val = e.target.value;
                  // Auto-detect participant name — first matching booking
                  // whose customer name appears (case-insensitive, min 3 chars)
                  // in the note gets pre-selected as the booking tag. Only
                  // runs when the partner hasn't already picked one.
                  let nextBookingId = form.booking_id;
                  let nextKind = form.booking_kind;
                  if (!form.booking_id && val.length >= 4) {
                    const lower = val.toLowerCase();
                    for (const b of bookings) {
                      const name = (b.customer || "").trim();
                      if (name.length >= 3 && lower.includes(name.toLowerCase())) {
                        nextBookingId = b.id; nextKind = b.kind;
                        toast.success(`Auto-tagged for ${name}`);
                        break;
                      }
                    }
                  }
                  setForm(f => ({ ...f, note: val, booking_id: nextBookingId, booking_kind: nextKind }));
                }}
                placeholder='e.g. "Feb tuition tranche for John Smith" — we auto-tag if we spot a participant name' className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="text-[10px] uppercase tracking-wider text-[color:var(--aii-text-2)] font-semibold">Attach receipt / screenshot (max {MAX_ATTACH_KB} KB)</label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" data-testid="pp-payment-file"/>
                <button onClick={pickFile} type="button" className="flex items-center gap-1.5 px-3 py-2 border border-[color:var(--aii-border)] bg-white rounded-md hover:bg-[color:var(--aii-green-tint)] text-sm">
                  <Paperclip size={14}/> {attachment ? attachment.name : "Choose file"}
                </button>
                {attachment && <button onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-xs text-[color:var(--aii-muted)]">Remove</button>}
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button data-testid="pp-payment-save" disabled={uploading} onClick={submit} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50">
              {uploading ? "Uploading…" : "Save payment"}
            </button>
          </div>
        </div>
      )}

      <div className="aii-card overflow-x-auto" data-testid="pp-payments-list">
        <table className="aii-table">
          <thead><tr>
            <th>Date</th><th>Participant</th><th>Programme</th><th>Amount</th><th>Method</th><th>Reference</th><th>Note</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[color:var(--aii-muted)] italic">No payments logged yet. Click <b>Log payment</b> above to record one.</td></tr>
            )}
            {rows.map(r => {
              const meta = statusChip(r.status);
              const Icon = meta.icon;
              return (
                <tr key={r.id} data-testid={`pp-payment-row-${r.id}`}>
                  <td className="text-sm">{fmtDate(r.entry_date)}</td>
                  <td className="text-xs font-medium" data-testid={`pp-log-participant-${r.id}`}>
                    {r.booking_customer || <span className="italic text-[color:var(--aii-muted)]">— unallocated —</span>}
                  </td>
                  <td className="text-xs" data-testid={`pp-log-programme-${r.id}`}>
                    {r.booking_programme || "—"}
                  </td>
                  <td className="font-semibold text-brand">{SYM[r.currency] || r.currency}{Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="uppercase text-xs">{r.method}</td>
                  <td className="text-xs max-w-[140px] truncate" title={r.reference}>{r.reference || "—"}</td>
                  <td className="text-xs max-w-[200px] truncate" title={r.note}>{r.note || "—"}</td>
                  <td>
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${meta.cls}`}>
                      <Icon size={10}/> {meta.label}
                    </span>
                  </td>
                  <td className="text-right">
                    <button onClick={() => del(r)} data-testid={`pp-payment-del-${r.id}`}
                            title={r.status === "verified" ? "Delete this verified payment (snapshot goes to Recycle Bin — Aii can restore)" : "Delete this payment log"}
                            className={`hover:bg-red-50 p-1 rounded ${r.status === "verified" ? "text-red-700 hover:text-white hover:bg-red-600 border border-red-300" : "text-[color:var(--aii-muted)] hover:text-red-600"}`}>
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {delModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setDelModal(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()} data-testid="payment-del-modal">
            <div className={`p-4 border-b ${delModal.row.status === "verified" ? "bg-red-50 border-red-200" : "border-[color:var(--aii-border)]"}`}>
              <h3 className="font-display text-lg font-semibold">
                Delete {SYM[delModal.row.currency] || delModal.row.currency}{delModal.row.amount} payment?
              </h3>
              <p className="text-xs mt-0.5 text-[color:var(--aii-muted)]">
                {delModal.row.status === "verified"
                  ? "This VERIFIED row snapshots to the Recycle Bin — your Aii coordinator will be notified for reconciliation."
                  : "Removes the pending payment log entry."}
              </p>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[color:var(--aii-muted)]">Why?</div>
              {PAYMENT_REASONS.map(r => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="pay-del-reason" data-testid={`pay-del-reason-${r.split(" ")[0].toLowerCase()}`}
                         checked={delModal.reason === r} onChange={() => setDelModal(s => ({ ...s, reason: r }))}/>
                  {r}
                </label>
              ))}
              {delModal.reason === "Other" && (
                <input data-testid="pay-del-note" value={delModal.note}
                       onChange={e => setDelModal(s => ({ ...s, note: e.target.value }))}
                       placeholder="Tell your coordinator what happened…" autoFocus
                       className="w-full mt-1 px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm"/>
              )}
            </div>
            <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-between gap-2">
              <button onClick={() => setDelModal(null)}
                      className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button data-testid="pay-del-skip" onClick={submitDel}
                        className="px-3 py-2 text-sm text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text-1)]">
                  Skip reason
                </button>
                <button
                  data-testid="pay-del-submit"
                  onClick={submitDel}
                  disabled={!delModal.reason || (delModal.reason === "Other" && !delModal.note.trim())}
                  className="px-4 py-2 text-sm bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {payAllPreview && (() => {
        // Waterfall preview — walks the rows in order, allocates each
        // booking's full outstanding balance until the pot runs dry.
        // Mirrors the backend allocator exactly so what the partner
        // sees here is byte-for-byte what will be posted.
        const rows = payAllPreview.rows;
        const target = payAllPreview.target;
        const totalPot = rows.reduce((t, r) => t + Number(r.balance ?? r.amount ?? 0), 0);
        let remaining = totalPot;
        const alloc = rows.map(r => {
          const need = Number(r.balance ?? r.amount ?? 0);
          const take = Math.min(remaining, need);
          remaining -= take;
          return { ...r, allocation: take };
        });
        const confirm = () => {
          setForm(f => {
            const ids = rows.map(r => r.id);
            const kinds = Object.fromEntries(rows.map(r => [r.id, r.kind]));
            return { ...f, booking_ids: ids, _kindsById: kinds,
                     booking_id: "", booking_kind: "",
                     currency: target, amount: String(Math.round(totalPot)) };
          });
          setPayAllPreview(null);
          toast.success(`${rows.length} bookings queued — review and hit Submit`);
        };
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
               data-testid="pp-pay-all-preview"
               onClick={() => setPayAllPreview(null)}>
            <div className="bg-white rounded-lg max-w-lg w-full p-5 shadow-xl max-h-[85vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Pay all due · preview</div>
              <h3 className="font-display font-semibold text-lg mt-0.5">
                {rows.length} bookings · {SYM[target] || target}{Math.round(totalPot).toLocaleString()}
              </h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-1">
                This is exactly how the payment will be split across each participant&apos;s outstanding balance. Nothing is submitted yet — hit Confirm to load these amounts into the form, then Submit as usual.
              </p>
              <ul className="mt-3 divide-y divide-slate-200 text-sm">
                {alloc.map(r => (
                  <li key={r.id} data-testid={`pp-preview-row-${r.id}`} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{r.kind === "weekend" ? "🗺️ " : "✈️ "}{r.customer}</div>
                      <div className="text-[11px] text-[color:var(--aii-muted)] truncate">
                        {r.programme} · {r.start_date || "—"}
                        {r.paid > 0 && <> · already paid {SYM[target] || target}{Math.round(r.paid).toLocaleString()}</>}
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-sm font-semibold text-emerald-800">{SYM[target] || target}{Math.round(r.allocation).toLocaleString()}</div>
                      <div className="text-[10px] text-slate-500">of {SYM[target] || target}{Math.round(r.balance ?? r.amount).toLocaleString()} due</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-slate-200">
                <div className="text-sm">
                  <span className="text-[color:var(--aii-muted)]">Total: </span>
                  <b className="text-brand">{SYM[target] || target}{Math.round(totalPot).toLocaleString()}</b>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPayAllPreview(null)} className="text-sm px-3 py-1.5 text-slate-600 hover:text-slate-900" data-testid="pp-preview-cancel">Cancel</button>
                  <button onClick={confirm} data-testid="pp-preview-confirm"
                          className="text-sm px-4 py-1.5 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark">
                    Confirm split
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
